import React, { useState } from "react";
import { Box, Button, CircularProgress, Dialog, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { Transition } from "../../UiComponents/Transition/Transition";
import { PutPolicyWalletMinimum } from "../../Service/_index";
import { isAtOrBelowMinimum } from "../../Shared/walletMinimum";
import { formatINR, sanitizeAmountInput } from "../AddMoneyToUser/addMoneyUtils";
import { FieldLabel, inputSx, navyButtonSx, outlinedButtonSx } from "../AddMoneyToUser/parts";
import Styles from "../AddMoneyToUser/AddMoneyToUser.module.scss";
import { minimumAmount, validateMinimum } from "./minimumBalanceUtils";

const paperProps = { sx: { maxWidth: "440px !important", width: "100%", borderRadius: "10px" } };

// What saving this value will do, in words, before the user presses Save.
const previewText = (text, balance) => {
  if (validateMinimum(text)) return "";
  const amount = minimumAmount(text);
  if (amount === 0) return "The warning will be turned off.";
  return isAtOrBelowMinimum(balance, amount)
    ? "Your balance is already at or below this, so the warning will show right away."
    : `You will be warned when your balance falls to ${formatINR(amount)} or less.`;
};

/**
 * Set (or change, or switch off with 0) the low-balance warning of the signed-in user's own Policy Wallet.
 * Render it only while it is open: it starts from `currentMinimum` each time. `onSaved(minimum)` gets the value the
 * server stored.
 */
const MinimumBalanceDialog = ({ currentMinimum, balance, onClose, onSaved }) => {
  const [text, setText] = useState(Number(currentMinimum) > 0 ? String(currentMinimum) : "");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  const error = validateMinimum(text);
  const showError = touched && error;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (error || saving) return;
    setSaving(true);
    setServerError("");
    const res = await PutPolicyWalletMinimum(minimumAmount(text));
    setSaving(false);
    if (res.error) {
      setServerError(res.message || "Could not save the minimum balance");
      return;
    }
    onSaved(res.data ? res.data.minimumBalance : minimumAmount(text));
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      TransitionComponent={Transition}
      PaperProps={paperProps}
      aria-labelledby="minimum-balance-title"
    >
      <Box className={Styles.DialogHeader}>
        <Typography id="minimum-balance-title" className={Styles.DialogTitle}>
          Minimum balance
        </Typography>
        <Typography className={Styles.DialogSubtitle}>Policy Wallet</Typography>
      </Box>
      <form onSubmit={handleSubmit} noValidate>
        <Box className={Styles.DialogBody}>
          <Typography sx={{ fontFamily: "Inter", fontSize: "13px", color: "#475467" }}>
            We will warn you in the bell at the top of the page when your Policy Wallet balance is at or below this
            amount. Enter 0 to turn the warning off.
          </Typography>
          <Typography sx={{ fontFamily: "Inter", fontSize: "13px", color: "#101828" }}>
            Your balance now: <strong>{formatINR(balance)}</strong>
          </Typography>
          <Box>
            <FieldLabel htmlFor="minimum-balance-input">Minimum balance (₹)</FieldLabel>
            <TextField
              id="minimum-balance-input"
              value={text}
              onChange={(event) => setText(sanitizeAmountInput(event.target.value))}
              onBlur={() => setTouched(true)}
              autoFocus
              fullWidth
              placeholder="0"
              error={Boolean(showError)}
              helperText={showError || previewText(text, balance) || " "}
              inputProps={{ inputMode: "decimal", autoComplete: "off", "aria-describedby": "minimum-balance-help" }}
              FormHelperTextProps={{ id: "minimum-balance-help" }}
              disabled={saving}
              sx={inputSx}
            />
          </Box>
          {serverError && (
            <Typography role="alert" sx={{ fontFamily: "Inter", fontSize: "12.5px", color: "#B42318" }}>
              {serverError}
            </Typography>
          )}
        </Box>
        <Box className={Styles.DialogFooter}>
          <Button variant="outlined" onClick={onClose} disabled={saving} sx={outlinedButtonSx}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : (
                <SaveIcon sx={{ fontSize: "16px !important" }} />
              )
            }
            sx={navyButtonSx}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default MinimumBalanceDialog;
